import { Injectable } from "@angular/core";
import { DateTime, Settings } from "luxon";
import { Adapter } from "src/app/core/adapter";
import { Model } from "src/app/core/models/model";

export enum MeterType {
  drac = "drac",
  drac_flowmeter = "drac_flowmeter",
  drac_mid = "drac_mid",
  v1 = "v1",
  v2 = "v2",
  sense = "sense",
  thirdparty_maddalena = "3p_maddalena",
  thirdparty_hac = "3p_hac",
  thirdparty_hac_itron = "3p_hac_itron",
  thirdparty_hac_diehl = "3p_hac_diehl",
  thirdparty_hac_sensus = "3p_hac_sensus",
  demo = "demo",
  thirdparty_adeunis = "3p_adeunis_pulse",
  thirdparty_quandify = "3p_quandify",
}

export const METER_DRAC_TYPES = [
  "drac",
  "drac_flowmeter",
  "drac_mid",
  "v2",
  "sense",
  "demo",
];

export const METER_3P_TYPES = [
  "3p_adeunis_pulse",
  "3p_hac",
  "3p_hac_diehl",
  "3p_hac_itron",
  "3p_maddalena",
  "3p_quandify",
];

export enum MeterFeature {
  conductivity = "conductivity", // sense only
  temperature = "temperature", // sense & v1
  hourly_signal = "hourly_signal", // all
  raw_signal = "raw_signal", // all\{3p, drac_mid}
  labelling = "labelling", // all\{3p}
  period_alerts = "period_alerts", // all
  smart_alerts = "smart_alerts", // all\{3p}
  threshold_alerts = "threshold_alerts", // all\{3p}
  timeseries_db = "timeseries_db", // all\{v1, demo}
  flow = "flow", // all\{3p}
  mqtt_signal = "mqtt_signal", // all
}

export const featuresByType = {
  [MeterType.drac]: [
    MeterFeature.hourly_signal,
    MeterFeature.period_alerts,
    MeterFeature.threshold_alerts,
    MeterFeature.flow,
  ],
  [MeterType.drac_flowmeter]: [
    MeterFeature.hourly_signal,
    MeterFeature.period_alerts,
    MeterFeature.threshold_alerts,
    MeterFeature.flow,
  ],
  [MeterType.drac_mid]: [
    MeterFeature.hourly_signal,
    MeterFeature.period_alerts,
    MeterFeature.threshold_alerts,
    MeterFeature.flow,
  ],
  [MeterType.v1]: [
    MeterFeature.temperature,
    MeterFeature.hourly_signal,
    MeterFeature.period_alerts,
    MeterFeature.threshold_alerts,
    MeterFeature.flow,
  ],
  [MeterType.v2]: [
    MeterFeature.hourly_signal,
    MeterFeature.period_alerts,
    MeterFeature.threshold_alerts,
    MeterFeature.flow,
  ],
  [MeterType.sense]: [
    MeterFeature.conductivity,
    MeterFeature.temperature,
    MeterFeature.hourly_signal,
    MeterFeature.period_alerts,
    MeterFeature.threshold_alerts,
    MeterFeature.flow,
  ],
  [MeterType.thirdparty_maddalena]: [
    MeterFeature.hourly_signal,
    MeterFeature.period_alerts,
  ],
  [MeterType.thirdparty_hac]: [
    MeterFeature.hourly_signal,
    MeterFeature.period_alerts,
  ],
  [MeterType.thirdparty_hac_itron]: [
    MeterFeature.hourly_signal,
    MeterFeature.period_alerts,
  ],
  [MeterType.thirdparty_hac_diehl]: [
    MeterFeature.hourly_signal,
    MeterFeature.period_alerts,
  ],
  [MeterType.thirdparty_hac_sensus]: [
    MeterFeature.hourly_signal,
    MeterFeature.period_alerts,
  ],
  [MeterType.demo]: [
    MeterFeature.hourly_signal,
    MeterFeature.period_alerts,
    MeterFeature.threshold_alerts,
    MeterFeature.flow,
  ],
  [MeterType.thirdparty_adeunis]: [
    MeterFeature.hourly_signal,
    MeterFeature.period_alerts,
  ],
  [MeterType.thirdparty_quandify]: [
    MeterFeature.hourly_signal,
    MeterFeature.period_alerts,
  ],
};

export const premiumFeaturesByType = {
  [MeterType.drac]: [
    MeterFeature.raw_signal,
    MeterFeature.labelling,
    MeterFeature.smart_alerts,
  ],
  [MeterType.drac_flowmeter]: [
    MeterFeature.raw_signal,
    MeterFeature.labelling,
    MeterFeature.smart_alerts,
  ],
  [MeterType.drac_mid]: [MeterFeature.labelling, MeterFeature.smart_alerts],
  [MeterType.v1]: [
    MeterFeature.raw_signal,
    MeterFeature.labelling,
    MeterFeature.smart_alerts,
  ],
  [MeterType.v2]: [
    MeterFeature.raw_signal,
    MeterFeature.labelling,
    MeterFeature.smart_alerts,
  ],
  [MeterType.sense]: [
    MeterFeature.raw_signal,
    MeterFeature.labelling,
    MeterFeature.smart_alerts,
  ],
  [MeterType.thirdparty_maddalena]: [],
  [MeterType.thirdparty_hac]: [],
  [MeterType.thirdparty_hac_itron]: [],
  [MeterType.thirdparty_hac_diehl]: [],
  [MeterType.thirdparty_hac_sensus]: [],
  [MeterType.demo]: [
    MeterFeature.raw_signal,
    MeterFeature.labelling,
    MeterFeature.smart_alerts,
  ],
  [MeterType.thirdparty_adeunis]: [],
  [MeterType.thirdparty_quandify]: [],
};

export const excludedFeatures = [
  // technical features
  "mqtt_signal",
  "timeseries_db",
  // basic features
  "hourly_signal",
  "period_alerts",
  "threshold_alerts",
  "flow",
];

export const MAX_DEEP_LEVEL = 4;

export type MeterStates = { [id: string]: boolean };

export interface MeterData {
  placeLabel: string;
  gatewayLabel: string;
}

export class Meter implements Model {
  // private utilsService: UtilsService = inject(UtilsService);

  // ATTENTION for each custom props => add to clone method too !!!!

  // for hierarchy needs
  public isAddPlaceholder: boolean = false;
  public isLoadingPlaceholder: boolean = false;
  public deepLevel: number = 0;
  public ancestorIds: string[] = [];
  public childrenIds: string[] = [];
  public deepChildrenIds: string[] = [];

  public placeLabel: string = null;
  public gatewayLabel: string = null;

  constructor(
    public device_uuid: string,
    public first_seen: DateTime,
    public last_seen: DateTime,
    public label: string,
    public serial: string,
    public last_update: DateTime,
    public place_id: number,
    public categories: string[],
    public features: MeterFeature[],
    public type: MeterType,
    public type_request: string,
    public charge_level: number,
    public parent_id: string,
    public gateway_uuid: string,
    public children: Array<Meter>
  ) {}

  public get universalLabel(): string {
    if (this.label && this.label.trim() !== "") return this.label.trim();
    if (this.serial && this.serial.trim() !== "") return this.serial.trim();
    return this.device_uuid;
  }

  private static _typeToLabel(type: string) {
    switch (type) {
      case MeterType.drac:
        return $localize`:@@meter.model.typeDrac:Drac`;
      case MeterType.drac_flowmeter:
        return $localize`:@@meter.model.typeDracFlowmeter:Drac flowmeter`;
      case MeterType.drac_mid:
        return $localize`:@@meter.model.typeDracMid:Drac MID`;
      case MeterType.v1:
        return $localize`:@@meter.model.typeV1:v1 meter`;
      case MeterType.v2:
        return $localize`:@@meter.model.typeV2:v2 meter`;
      case MeterType.sense:
        return $localize`:@@meter.model.typeSense:HYDRAO Sense`;
      case MeterType.thirdparty_maddalena:
        return $localize`:@@meter.model.typeMaddalena:Maddalena`;
      case MeterType.thirdparty_hac:
        return $localize`:@@meter.model.typeHac:Radio Module`;
      case MeterType.thirdparty_hac_diehl:
        return $localize`:@@meter.model.typeHacDiehl:Radio Module for Diehl`;
      case MeterType.thirdparty_hac_itron:
        return $localize`:@@meter.model.typeHacItron:Radio Module for Itron`;
      case MeterType.thirdparty_hac_sensus:
        return $localize`:@@meter.model.typeHacSensus:HAC Sensus`;
      case MeterType.demo:
        return $localize`:@@meter.model.typeDemo:Demo meter`;
      case MeterType.thirdparty_adeunis:
        return $localize`:@@meter.model.typeAdeunis:Adeunis Pulse`;
      case MeterType.thirdparty_quandify:
        return $localize`:@@meter.model.typeQuandify:Quandify`;
      default:
        return $localize`:@@meter.model.unknownType:unknown`;
    }
  }

  public static featureToLabel(feature: MeterFeature) {
    switch (feature) {
      case MeterFeature.conductivity:
        return $localize`conductivity`;
      case MeterFeature.hourly_signal:
        return $localize`hourly signal`;
      case MeterFeature.labelling:
        return $localize`detailed use`;
      case MeterFeature.period_alerts:
        return $localize`period alerts`;
      case MeterFeature.raw_signal:
        return $localize`raw signal`;
      case MeterFeature.smart_alerts:
        return $localize`smart alerts`;
      case MeterFeature.temperature:
        return $localize`temperature`;
      case MeterFeature.threshold_alerts:
        return $localize`threshold alerts`;
      case MeterFeature.flow:
        return $localize`flow`;
      // case MeterFeature.mqtt_signal:
      //   return $localize`mqtt signal`;
      // case MeterFeature.timeseries_db:
      //   return $localize`timeseries db`;
      default:
        return $localize`unknown`;
    }
  }

  public get isDracLike(): boolean {
    return METER_DRAC_TYPES.indexOf(this.type) !== -1;
  }

  public get is3pLike(): boolean {
    return METER_3P_TYPES.indexOf(this.type) !== -1;
  }

  public get typeLabel(): string {
    return Meter._typeToLabel(this.type);
  }

  public get typeRequestLabel(): string {
    return Meter._typeToLabel(this.type_request);
  }

  public get typeRequestPending(): boolean {
    return this.type_request && this.type !== this.type_request;
  }

  public get lastSeenDuration(): number {
    if (!this.last_seen || this.last_seen === null) return 0;

    return (
      (DateTime.local().toMillis() - this.last_seen.toMillis()) / 1000 / 60
    );
  }

  public seenRelative(relativeDate: string) {
    return $localize`:@@meter.model.lastSeen:last seen` + " " + relativeDate;
  }

  public lastSeenRelative(locale: string = $localize.locale) {
    return this.last_seen !== null
      ? this.last_seen.setLocale(locale).toRelative()
      : $localize`never seen`;
  }

  public get isDrac(): boolean {
    return (
      this.type == MeterType.drac ||
      this.type == MeterType.drac_flowmeter ||
      this.type == MeterType.drac_mid
    );
  }

  public get isDracMid(): boolean {
    return this.type == MeterType.drac_mid;
  }

  public get isSense(): boolean {
    return this.type == MeterType.sense;
  }

  public get canViewHourlySignal(): boolean {
    return (
      this.features && this.features.indexOf(MeterFeature.hourly_signal) >= 0
    );
  }

  public get canViewRawSignal(): boolean {
    return this.features && this.features.indexOf(MeterFeature.raw_signal) >= 0;
  }

  public get canViewMeterUsage(): boolean {
    return this.features && this.features.indexOf(MeterFeature.labelling) >= 0;
  }

  public get canViewTemperature(): boolean {
    return (
      this.features && this.features.indexOf(MeterFeature.temperature) >= 0
    );
  }

  public get canViewConductivity(): boolean {
    return (
      this.features && this.features.indexOf(MeterFeature.conductivity) >= 0
    );
  }

  public get canUseTSDB(): boolean {
    return (
      this.features && this.features.indexOf(MeterFeature.timeseries_db) >= 0
    );
  }

  public get canViewFlow(): boolean {
    return this.features && this.features.indexOf(MeterFeature.flow) >= 0;
  }

  public clone(): Meter {
    const children = [];
    if (this.children && this.children.length > 0) {
      this.children.map((child: Meter) => {
        children.push(child.clone());
      });
    }
    const clone = new Meter(
      this.device_uuid,
      this.first_seen,
      this.last_seen,
      this.label,
      this.serial,
      this.last_update,
      this.place_id,
      Array.isArray(this.categories) ? [...this.categories] : [],
      Array.isArray(this.features) ? [...this.features] : [],
      this.type,
      this.type_request,
      this.charge_level,
      this.parent_id,
      this.gateway_uuid,
      children
    );

    clone.ancestorIds = Array.isArray(this.ancestorIds)
      ? [...this.ancestorIds]
      : [];
    clone.childrenIds = Array.isArray(this.childrenIds)
      ? [...this.childrenIds]
      : [];
    clone.deepChildrenIds = Array.isArray(this.deepChildrenIds)
      ? [...this.deepChildrenIds]
      : [];
    clone.isAddPlaceholder = this.isAddPlaceholder;
    clone.isLoadingPlaceholder = this.isLoadingPlaceholder;
    clone.deepLevel = this.deepLevel;
    clone.placeLabel = this.placeLabel;
    clone.gatewayLabel = this.gatewayLabel;

    return clone;
  }

  public setData(data: MeterData) {
    if (!data) return;

    this.placeLabel = data.placeLabel;
    this.gatewayLabel = data.gatewayLabel;
  }
}

@Injectable({
  providedIn: "root",
})
export class MeterAdapter implements Adapter<Meter> {
  adapt(item: any): Meter {
    return new Meter(
      item.device_uuid,
      item.first_seen
        ? DateTime.fromISO(item.first_seen, { zone: Settings.defaultZone })
        : null,
      item.last_seen
        ? DateTime.fromISO(item.last_seen, { zone: Settings.defaultZone })
        : null,
      item.label,
      item.serial,
      item.last_update
        ? DateTime.fromISO(item.last_update, { zone: Settings.defaultZone })
        : null,
      item.place_id,
      item.categories,
      item.features,
      item.type,
      item.type_request,
      item.charge_level,
      item.parent_id,
      item.gateway_uuid,
      Array.isArray(item.children)
        ? item.children.map((i) => this.adapt(i))
        : null
    );
  }
}
